import type { MapDocument, MapMeta } from './types.ts';

export const MAX_MAP_AUTHOR_LENGTH = 80;
export const MAX_MAP_REVISION_LENGTH = 32;
export const MAX_MAP_DESCRIPTION_LENGTH = 280;
export const MAX_MAP_NOTES_LENGTH = 2000;

const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;
const CONTROL_CHARS_KEEP_NEWLINES = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

export function emptyMapMeta(): MapMeta {
  return { author: '', revision: '', description: '', notes: '' };
}

function sanitizeLine(raw: string, maxLength: number): string {
  return raw.replace(CONTROL_CHARS, '').replace(/\s+/g, ' ').trim().slice(0, maxLength);
}

function sanitizeNotes(raw: string): string {
  return raw
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(CONTROL_CHARS_KEEP_NEWLINES, '')
    .trim()
    .slice(0, MAX_MAP_NOTES_LENGTH);
}

export function sanitizeMapMeta(raw: Partial<MapMeta>): MapMeta {
  return {
    author: sanitizeLine(raw.author ?? '', MAX_MAP_AUTHOR_LENGTH),
    revision: sanitizeLine(raw.revision ?? '', MAX_MAP_REVISION_LENGTH),
    description: sanitizeLine(raw.description ?? '', MAX_MAP_DESCRIPTION_LENGTH),
    notes: sanitizeNotes(raw.notes ?? ''),
  };
}

export function setMapMeta(map: MapDocument, patch: Partial<MapMeta>): void {
  map.meta = sanitizeMapMeta({ ...map.meta, ...patch });
}

export function mapMetaEquals(a: MapMeta, b: MapMeta): boolean {
  return (
    a.author === b.author &&
    a.revision === b.revision &&
    a.description === b.description &&
    a.notes === b.notes
  );
}

export function migrateMapMeta(value: unknown): MapMeta {
  if (typeof value !== 'object' || value === null) {
    return emptyMapMeta();
  }

  const raw = value as Record<string, unknown>;
  return sanitizeMapMeta({
    author: typeof raw.author === 'string' ? raw.author : '',
    revision: typeof raw.revision === 'string' ? raw.revision : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    notes: typeof raw.notes === 'string' ? raw.notes : '',
  });
}
